import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Layers, Package } from 'lucide-react';
import type { Product } from '@/services/types';
import { Badge, CountBubble } from '@/components/ui';
import { formatCurrency } from '@/utils/formatters';

export interface ProductTileProps {
    product: Product;
    qtyInCart?: number;
    onClick: (product: Product) => void;
}

export const LOW_STOCK = 5;

/** O1 product card. The whole tile is the add button; parents with variants open the picker. */
export const ProductTile: React.FC<ProductTileProps> = ({ product, qtyInCart = 0, onClick }) => {
    const { t } = useTranslation();
    const hasVariants = !!product.variants?.length;
    // A parent at 0 may still have in-stock variants, so only disable on own stock when there are none.
    const enabled = hasVariants || product.availableQty > 0;
    const low = !hasVariants && enabled && product.availableQty <= LOW_STOCK;

    return (
        <button
            type="button"
            disabled={!enabled}
            onClick={() => onClick(product)}
            className={clsx(
                'relative flex flex-col overflow-hidden rounded-lg border border-line-subtle bg-surface-card text-left shadow-sm transition-[transform,box-shadow] duration-200',
                enabled ? 'active:scale-[.98] hover:shadow-md' : 'cursor-not-allowed',
            )}
        >
            <div className="flex h-[92px] items-center justify-center bg-surface-sunken text-primary-300 lg:h-[84px]">
                {product.image ? (
                    <img src={product.image} alt="" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                ) : (
                    <Package className="h-[34px] w-[34px] lg:h-8 lg:w-8" />
                )}
            </div>
            {qtyInCart > 0 && <CountBubble count={qtyInCart} tone="accent" className="absolute right-2 top-2" />}
            {hasVariants && (
                <span className="absolute left-2 top-2 flex h-6 items-center gap-1 rounded-pill bg-surface-card px-[9px] text-[11px] font-extrabold text-ink-brand shadow-xs">
                    <Layers className="h-3 w-3" />
                    {t('catalog.variantCount', { count: product.variants!.length })}
                </span>
            )}
            {!enabled && (
                <div className="absolute inset-0 flex items-start p-2 bg-[rgba(250,248,249,.62)]">
                    <Badge tone="danger">{t('catalog.outOfStock')}</Badge>
                </div>
            )}
            <div className="flex flex-col gap-1.5 p-[10px_12px_12px]">
                <span className="line-clamp-2 min-h-[36px] text-sm font-bold leading-[1.3] lg:min-h-0 lg:truncate">{product.name}</span>
                <span className="flex items-baseline justify-between gap-1.5">
                    <span className="text-[15px] font-black tnum text-ink-brand">{formatCurrency(product.price)}</span>
                    {!hasVariants && (
                        <span className={clsx('text-[11px] tnum', low ? 'font-extrabold text-accent-700' : 'font-bold text-ink-muted')}>
                            {low ? t('catalog.remaining', { count: product.availableQty }) : t('catalog.stockCount', { count: product.availableQty })}
                        </span>
                    )}
                </span>
            </div>
        </button>
    );
};
export default ProductTile;
